import React from "react";
import ClinicianCard from "./ClinicianCard";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import ClientBlock from "./ClientBlock";
import ApproveAllDialog from "./ApproveAllDialog";
import useScheduleVideoCall from "hooks/api/appointments/useScheduleVideoCall";
import { useMatching } from "components/Matching/MatchingProvider";
import useAssignClinician from "hooks/api/customers/useAssignClinician";

export default function MatchCard({ generatedSchedule, setShowCaseload }) {
  const [isSuccessful, setIsSuccessful] = React.useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const { isScheduleVideoCallLoading, scheduleVideoCall } = useScheduleVideoCall();
  const { assignClinicianAsync, isAssignClinicianLoading } = useAssignClinician();
  const {
    selectedClinician,
    setSelectedClinician,
    selectedClient,
    setSelectedClient,
    generateApproveSchedulePayload,
    refetchGeneratedSchedules,
  } = useMatching();

  const collapsed = generatedSchedule.clinician_id !== selectedClinician;

  const approveClinicianSchedule = async (isTentative = false) => {
    setApproveDialogOpen(false);
    setSelectedClinician(null);

    const { payload, clientIds } = await generateApproveSchedulePayload({
      setTentative: isTentative,
    });

    scheduleVideoCall(payload, {
      onSuccess: async () => {
        try {
          await Promise.all(
            clientIds.map((clientId) =>
              assignClinicianAsync({ clientId, clinicianUserId: payload?.[0]?.clinicianUserId })
            )
          );

          setIsSuccessful(true);
          refetchGeneratedSchedules();
        } catch (error) {
          console.error("Failed to assign clinicians:");
        }
      },
    });
  };

  const openApproveDialog = (e) => {
    e.stopPropagation(); // Prevent the clinician card from collapsing
    setApproveDialogOpen(true);
  };

  const handleClinicianCardClick = () => {
    setSelectedClinician(!collapsed ? null : generatedSchedule.clinician_id);
    setSelectedClient(null);
  };

  return (
    <Stack>
      <ClinicianCard
        collapsed={collapsed}
        onClick={handleClinicianCardClick}
        generatedSchedule={generatedSchedule}
        isLoading={isScheduleVideoCallLoading || isAssignClinicianLoading}
        isSuccessful={isSuccessful}
        onApproveAll={openApproveDialog}
        onSetTentative={() => approveClinicianSchedule(true)}
        setShowCaseload={setShowCaseload}
      />
      <Collapse in={!collapsed} timeout="auto">
        <Box
          pt={9}
          px={5}
          bgcolor="default.hover"
          position="relative"
          zIndex={-1}
          top="-16px"
          mb={-5}
        >
          <Typography variant="caption" color="text.secondary">
            Clients matches
          </Typography>
        </Box>
        <Stack bgcolor="default.hover" borderRadius="0 0 16px 16px">
          {generatedSchedule.client_schedules.map((client_schedule, index) => {
            const isLastItem = index === generatedSchedule.client_schedules.length - 1;

            return (
              <ClientBlock
                key={client_schedule.client_id}
                clientSchedule={client_schedule}
                selected={selectedClient === client_schedule.client_id}
                clinicianName={generatedSchedule.clinician_name}
                onEyeButtonClick={() =>
                  setSelectedClient(
                    selectedClient === client_schedule.client_id ? null : client_schedule.client_id
                  )
                }
                borderRadius={isLastItem ? "0 0 16px 16px" : "none"}
              />
            );
          })}
        </Stack>
      </Collapse>
      <ApproveAllDialog
        onApproveAll={() => approveClinicianSchedule()}
        open={approveDialogOpen}
        handleClose={() => setApproveDialogOpen(false)}
      />
    </Stack>
  );
}
